import {
  useDirtyState,
  useScopeResolution,
  useScopeTransition,
} from '@/hooks/useScopeChangeHooks';
import { useEffect, type ReactNode } from 'react';

interface ScopeChangeGuardProviderProps {
  children: ReactNode;
  enabled: boolean;
}

export const ScopeChangeGuardProvider = ({
  children,
  enabled,
}: ScopeChangeGuardProviderProps) => {
  const { checkScopeIsDirty } = useDirtyState();
  const { pendingTransition, clearTransition } = useScopeTransition();

  const { requireResolution } = useScopeResolution();

  useEffect(() => {
    if (!pendingTransition) return;
    const isDirty = checkScopeIsDirty(pendingTransition.from);
    if (!enabled || !isDirty) {
      pendingTransition.run();
      clearTransition();
      return;
    }

    // Hand off to the dialog and drop it from the pending store so this effect
    // can't re-fire on a later dirty-clear and silently run the navigation while
    // the dialog is still open.
    requireResolution(pendingTransition);
    clearTransition();
  }, [
    pendingTransition,
    checkScopeIsDirty,
    enabled,
    requireResolution,
    clearTransition,
  ]);

  return <>{children}</>;
};
